/**
 * COLUMN-RENDER-1 — display cells shared by every project task table
 * (WaterfallTable and TaskListSection). One definition of what each column
 * shows, so a column cannot look different (or be missing) depending on which
 * table a project's methodology picked.
 *
 * Display only. Editing stays with the table that owns the edit handlers.
 * An empty value is an em dash, never a blank or a raw id.
 */
import React from 'react';

import type { SharedCellColumn } from './shared-cell-columns';
import { parentTitleFor } from './shared-cell-columns';

import { WorkTaskTypeBadge } from '@/features/work-management/components/WorkTaskTypeBadge';
import type { WorkTask } from '@/features/work-management/workTasks.api';


const EMPTY = <span className="text-slate-300 dark:text-slate-600">—</span>;

/** Truncated text with the full value on hover. */
function Clipped({ text, max }: { text: string; max: number }) {
  const short = text.length > max ? `${text.slice(0, max)}…` : text;
  return (
    <span className="text-slate-600 dark:text-slate-300" title={text}>
      {short}
    </span>
  );
}

function stamp(iso: string | null | undefined): React.ReactNode {
  if (!iso) return EMPTY;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? EMPTY : d.toLocaleDateString();
}

export function SharedCell({
  column,
  task,
  loadedTasks,
}: {
  column: SharedCellColumn;
  task: WorkTask;
  loadedTasks?: ReadonlyArray<Pick<WorkTask, 'id' | 'title'>>;
}): React.ReactElement {
  switch (column) {
    case 'priority':
      return <span className="text-slate-700 dark:text-slate-200">{task.priority}</span>;
    case 'description': {
      const d = task.description?.trim();
      return d ? <Clipped text={d} max={100} /> : EMPTY;
    }
    case 'tags': {
      const tags = task.tags ?? [];
      return tags.length > 0 ? <Clipped text={tags.join(', ')} max={60} /> : EMPTY;
    }
    case 'taskType':
      return <WorkTaskTypeBadge type={task.type} />;
    case 'estimateHours':
      return task.estimateHours != null ? <span className="tabular-nums">{task.estimateHours}</span> : EMPTY;
    case 'actualHours':
      return task.actualHours != null ? <span className="tabular-nums">{task.actualHours}</span> : EMPTY;
    case 'storyPoints':
      return task.estimatePoints != null ? <span className="tabular-nums">{task.estimatePoints}</span> : EMPTY;
    case 'dateCreated':
      return <>{stamp(task.createdAt)}</>;
    case 'dateUpdated':
      return <>{stamp(task.updatedAt)}</>;
    case 'dateDone':
      return <>{stamp(task.completedAt)}</>;
    case 'parentTask': {
      const title = parentTitleFor(task, loadedTasks);
      return title ? <Clipped text={title} max={40} /> : EMPTY;
    }
    case 'dependencies': {
      const count = task.dependencyCount ?? 0;
      const titles = task.dependencyTitles ?? [];
      if (count === 0) return EMPTY;
      const shown = titles.map((t) => t.title).join(', ');
      const more = count > titles.length ? ` +${count - titles.length} more` : '';
      return (
        <span
          className="text-slate-600 dark:text-slate-300"
          title={`Depends on: ${shown}${more}`}
          data-testid={`dependencies-cell-${task.id}`}
        >
          {count} {count === 1 ? 'task' : 'tasks'}
          <span className="ml-1 text-slate-400 dark:text-slate-500">{titles[0]?.title ? `· ${titles[0].title}` : ''}</span>
        </span>
      );
    }
  }
}
